import React, { cloneElement, isValidElement } from 'react';

import { Step, StepProps } from '@/shared/components/stepper/step';
import { cn } from '@/shared/utils/cn';

const MOBILE_BREAKPOINT = 960;

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(
    undefined,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener('change', onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return !!isMobile;
}

/* -------------------------------------------------------------------------- */
/* 1. Public-facing props for *one* step                                      */
/*    Everything that the Stepper itself will inject is removed.              */
/* -------------------------------------------------------------------------- */
export type StepperItemProps = Omit<
  StepProps,
  'index' | 'currentStep' | 'totalSteps' | 'orientation'
>;

/* A tiny wrapper so that <Stepper.Step …> only needs the trimmed props */
const StepperItem: React.FC<StepperItemProps> = (props) => (
  /*  Type-cast is safe because Stepper will fill the missing fields  */
  <Step {...(props as StepProps)} />
);

/* -------------------------------------------------------------------------- */
/* 2. Stepper props                                                           */
/* -------------------------------------------------------------------------- */
export type StepperProps = React.HTMLAttributes<HTMLUListElement> & {
  currentStep: number; // 1-based
  orientation?: 'vertical' | 'horizontal';
  children:
    | React.ReactElement<StepperItemProps>
    | React.ReactElement<StepperItemProps>[];
};

/* -------------------------------------------------------------------------- */
/* 3. Component                                                               */
/* -------------------------------------------------------------------------- */
export const Stepper = ({
  currentStep,
  orientation: orientationProp,
  children,
  className,
  ...props
}: StepperProps) => {
  const isMobile = useIsMobile();

  const orientation = orientationProp ?? (isMobile ? 'horizontal' : 'vertical');
  /* normalise children to an array  */
  const items = React.Children.toArray(children).filter(
    isValidElement,
  ) as React.ReactElement<StepperItemProps>[];

  const totalSteps = items.length;

  /* clone each child and add the missing bookkeeping props */
  const enhanced = items.map((child, idx) =>
    cloneElement(child, {
      index: idx + 1,
      currentStep,
      totalSteps,
      orientation,
    } as StepProps),
  );

  return (
    <ul
      {...props}
      className={cn(orientation === 'horizontal' && 'flex w-full', className)}
    >
      {enhanced}
    </ul>
  );
};

/* -------------------------------------------------------------------------- */
/* 4. Re-export so that users can write <Stepper.Step …>                      */
/* -------------------------------------------------------------------------- */
Stepper.Step = StepperItem;
